import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { Calendar, Trash2, Plus } from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

/* ---------- Holiday helpers ---------- */
function nthWeekdayOfMonth(year, month, weekday, n) {
  // n = 1..5, weekday = 0..6 (Sun..Sat)
  const first = new Date(year, month, 1);
  const shift = ((weekday - first.getDay()) + 7) % 7;
  return new Date(year, month, 1 + shift + (n - 1) * 7);
}
function lastWeekdayOfMonth(year, month, weekday) {
  const d = new Date(year, month + 1, 0);
  const shift = ((d.getDay() - weekday) + 7) % 7;
  d.setDate(d.getDate() - shift);
  return d;
}
function iso(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }

// US federal holidays for a given year — used by default
function usFederalHolidays(year) {
  return [
    { name: "New Year's Day", date: iso(new Date(year, 0, 1)) },
    { name: 'MLK Day', date: iso(nthWeekdayOfMonth(year, 0, 1, 3)) },       // 3rd Mon Jan
    { name: "Presidents' Day", date: iso(nthWeekdayOfMonth(year, 1, 1, 3)) }, // 3rd Mon Feb
    { name: 'Memorial Day', date: iso(lastWeekdayOfMonth(year, 4, 1)) },   // last Mon May
    { name: 'Juneteenth', date: iso(new Date(year, 5, 19)) },
    { name: 'Independence Day', date: iso(new Date(year, 6, 4)) },
    { name: 'Labor Day', date: iso(nthWeekdayOfMonth(year, 8, 1, 1)) },    // 1st Mon Sep
    { name: 'Columbus Day', date: iso(nthWeekdayOfMonth(year, 9, 1, 2)) }, // 2nd Mon Oct
    { name: 'Veterans Day', date: iso(new Date(year, 10, 11)) },
    { name: 'Thanksgiving Day', date: iso(nthWeekdayOfMonth(year, 10, 4, 4)) }, // 4th Thu Nov
    { name: 'Christmas Day', date: iso(new Date(year, 11, 25)) },
  ];
}

export default function DayCounter() {
  const now = new Date();
  const [sM, setSM] = useState(now.getMonth());
  const [sD, setSD] = useState(now.getDate());
  const [sY, setSY] = useState(now.getFullYear());
  const [eM, setEM] = useState(now.getMonth());
  const [eD, setED] = useState(now.getDate());
  const [eY, setEY] = useState(now.getFullYear() + 1);
  const [includeEnd, setIncludeEnd] = useState(false);
  const [countMode, setCountMode] = useState('count'); // count | skip

  // Holidays — seeded with US federal for years covered; user can add/remove
  const [holidayPreset, setHolidayPreset] = useState('us');
  const [customHolidays, setCustomHolidays] = useState([]); // [{name, date}]
  const [hName, setHName] = useState('');
  const [hDate, setHDate] = useState('');

  const [addM, setAddM] = useState(now.getMonth());
  const [addD, setAddD] = useState(now.getDate());
  const [addY, setAddY] = useState(now.getFullYear());
  const [op, setOp] = useState('+');
  const [amount, setAmount] = useState(15);
  const [businessOnly, setBusinessOnly] = useState(false);

  const holidays = useMemo(() => {
    const start = new Date(sY, sM, sD);
    const end = new Date(eY, eM, eD);
    if (isNaN(start) || isNaN(end)) return [];
    let list = [];
    const yFrom = Math.min(start.getFullYear(), end.getFullYear());
    const yTo = Math.max(start.getFullYear(), end.getFullYear());
    if (holidayPreset === 'us') {
      for (let y = yFrom; y <= yTo; y++) list = list.concat(usFederalHolidays(y));
    }
    list = list.concat(customHolidays.filter(h => h && h.date));
    // Dedupe by ISO date
    const seen = new Set();
    return list.filter(h => { if (seen.has(h.date)) return false; seen.add(h.date); return true; });
  }, [sM, sD, sY, eM, eD, eY, holidayPreset, customHolidays]);

  const holidaySet = useMemo(() => new Set(holidays.map(h => h.date)), [holidays]);

  const result = useMemo(() => {
    const start = new Date(sY, sM, sD);
    const end = new Date(eY, eM, eD);
    if (isNaN(start) || isNaN(end)) return null;
    let total = Math.round((end - start) / 86400000);
    if (includeEnd) total += 1;
    let biz = 0, wknd = 0, holCount = 0;
    const s = new Date(start), e = new Date(end);
    while (s <= e) {
      const dow = s.getDay();
      const isWknd = dow === 0 || dow === 6;
      const isHol = holidaySet.has(iso(s));
      if (isHol) holCount++;
      if (isWknd) wknd++;
      else if (countMode === 'skip' && isHol) { /* holiday on weekday counted as non-business */ }
      else biz++;
      s.setDate(s.getDate() + 1);
    }
    return { total, biz, wknd, holCount, weeks: (total/7).toFixed(2) };
  }, [sM, sD, sY, eM, eD, eY, includeEnd, holidaySet, countMode]);

  const shifted = useMemo(() => {
    const d = new Date(addY, addM, addD);
    const delta = op === '+' ? amount : -amount;
    if (!businessOnly) { d.setDate(d.getDate() + delta); return d; }
    let remaining = Math.abs(delta); const step = delta >= 0 ? 1 : -1;
    while (remaining > 0) {
      d.setDate(d.getDate() + step);
      const dw = d.getDay();
      if (dw === 0 || dw === 6) continue;
      if (countMode === 'skip' && holidaySet.has(iso(d))) continue;
      remaining--;
    }
    return d;
  }, [addM, addD, addY, op, amount, businessOnly, holidaySet, countMode]);

  const DateInputs = ({ m, setMFn, d, setDFn, y, setYFn }) => (
    <div className="flex items-center gap-2">
      <select value={m} onChange={e => setMFn(+e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm">{MONTHS.map((mo,i)=><option key={i} value={i}>{mo}</option>)}</select>
      <select value={d} onChange={e => setDFn(+e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm">{Array.from({length:31}, (_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}</select>
      <select value={y} onChange={e => setYFn(+e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm">{Array.from({length:60}, (_,i)=>2000+i).map(yr=><option key={yr} value={yr}>{yr}</option>)}</select>
      <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center text-white"><Calendar className="w-4 h-4" /></div>
    </div>
  );

  const addCustom = () => {
    if (!hDate || !hName) return;
    setCustomHolidays(list => [...list, { name: hName, date: hDate }].sort((a,b) => a.date.localeCompare(b.date)));
    setHName(''); setHDate('');
  };
  const removeCustom = (idx) => setCustomHolidays(list => list.filter((_,i) => i !== idx));

  return (
    <CalcShell look="cnet" title="Day Counter" subtitle="Count total days, business days, weekends and holidays between dates. Add or subtract days with optional holiday awareness.">
      <h3 className="text-xl font-bold text-slate-900 mb-4">Count Days Between Dates</h3>
      <div className="space-y-3">
        <div className="flex items-center gap-4 flex-wrap"><label className="w-24 text-sm font-semibold">Start Date</label><DateInputs m={sM} setMFn={setSM} d={sD} setDFn={setSD} y={sY} setYFn={setSY} /></div>
        <div className="flex items-center gap-4 flex-wrap"><label className="w-24 text-sm font-semibold">End Date</label><DateInputs m={eM} setMFn={setEM} d={eD} setDFn={setED} y={eY} setYFn={setEY} /></div>
        <label className="flex items-center gap-2 text-sm text-slate-700 ml-28"><input type="checkbox" checked={includeEnd} onChange={e => setIncludeEnd(e.target.checked)} data-testid="dc-include-end" /> Include end day (+1 day)</label>

        <div className="flex items-center gap-4 ml-28 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="radio" name="dc-hol" checked={countMode === 'count'} onChange={() => setCountMode('count')} data-testid="dc-count-hol" /> Count holidays as business days
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="radio" name="dc-hol" checked={countMode === 'skip'} onChange={() => setCountMode('skip')} data-testid="dc-skip-hol" /> Do not count holidays
          </label>
        </div>

        <div className="ml-28 mt-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
          <div className="flex items-center justify-between mb-2">
            <b className="text-sm">Holiday list</b>
            <select value={holidayPreset} onChange={e => setHolidayPreset(e.target.value)} className="px-2 py-1 border rounded text-xs bg-white" data-testid="dc-preset">
              <option value="us">US Federal Holidays</option>
              <option value="none">None (custom only)</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs mb-2 max-h-40 overflow-y-auto" data-testid="dc-holiday-list">
            {holidays.map((h, i) => (
              <div key={h.date + i} className="flex items-center justify-between px-2 py-1 bg-white rounded border border-slate-100">
                <span><b>{h.date}</b> — {h.name}</span>
              </div>
            ))}
            {!holidays.length && <div className="text-slate-500 italic">No holidays in range.</div>}
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex flex-col text-xs">
              <label className="text-slate-500 mb-0.5">Holiday name</label>
              <input value={hName} onChange={e => setHName(e.target.value)} placeholder="e.g. Company Holiday" className="px-2 py-1 border rounded" data-testid="dc-hol-name" />
            </div>
            <div className="flex flex-col text-xs">
              <label className="text-slate-500 mb-0.5">Date</label>
              <input type="date" value={hDate} onChange={e => setHDate(e.target.value)} className="px-2 py-1 border rounded" data-testid="dc-hol-date" />
            </div>
            <button onClick={addCustom} data-testid="dc-add-hol" className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs flex items-center gap-1"><Plus className="w-3 h-3" /> Add holiday</button>
          </div>
          {customHolidays.length > 0 && (
            <div className="mt-2 text-xs">
              <b>Custom holidays:</b>
              <ul className="mt-1 space-y-1">
                {customHolidays.map((h, i) => (
                  <li key={h.date + i} className="flex items-center justify-between bg-white px-2 py-1 rounded">
                    <span>{h.date} — {h.name}</span>
                    <button onClick={() => removeCustom(i)} className="text-red-500" data-testid={`dc-remove-hol-${i}`}><Trash2 className="w-3 h-3" /></button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {result && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Stat label="Total days" value={result.total} color="blue" />
          <Stat label="Business days" value={result.biz} color="green" />
          <Stat label="Weekend days" value={result.wknd} color="orange" />
          <Stat label={countMode === 'skip' ? 'Holidays (excluded)' : 'Holidays (included)'} value={result.holCount} color="purple" />
        </div>
      )}

      <div className="mt-8 border-t border-slate-200 pt-6">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Count Days from a Date</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-4 flex-wrap"><label className="w-24 text-sm font-semibold">Start Date</label><DateInputs m={addM} setMFn={setAddM} d={addD} setDFn={setAddD} y={addY} setYFn={setAddY} /></div>
          <div className="flex items-center gap-4 flex-wrap"><label className="w-24 text-sm font-semibold">Operation</label>
            <select value={op} onChange={e => setOp(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm w-20"><option>+</option><option>-</option></select>
            <input type="number" value={amount} onChange={e => setAmount(+e.target.value || 0)} className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm w-24" />
            <span className="text-sm text-slate-600">days</span>
          </div>
          <label className="flex items-center gap-2 text-sm ml-28"><input type="checkbox" checked={businessOnly} onChange={e => setBusinessOnly(e.target.checked)} data-testid="dc-biz-only" /> Count business days only (uses holiday setting above)</label>
        </div>
        <div className="result-panel mt-4"><div className="result-title">Result Date</div><div className="result-value text-2xl" data-testid="dc-shifted-result">{shifted.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div></div>
      </div>
    </CalcShell>
  );
}

function Stat({ label, value, color }) {
  const cmap = { blue: 'bg-blue-50 text-blue-700 border-blue-200', green: 'bg-emerald-50 text-emerald-700 border-emerald-200', orange: 'bg-orange-50 text-orange-700 border-orange-200', purple: 'bg-violet-50 text-violet-700 border-violet-200' };
  return <div className={`rounded-xl p-4 border ${cmap[color]}`}><div className="text-xs font-medium opacity-80">{label}</div><div className="text-2xl font-extrabold mt-1">{value}</div></div>;
}
